<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

export interface SearchableMultiselectItem {
  id: string | number;
  name: string;
  color?: string;
  disabled?: boolean;
}

const DEFAULT_COLOR = "4ECDC4";

interface Props {
  items: SearchableMultiselectItem[];
  modelValue: (string | number)[];
  searchPlaceholder?: string;
  disabled?: boolean;
  useSecondaryColor?: boolean;
  multiple?: boolean;
  dotOnly?: boolean;
  isFetchingNextPage?: boolean;
  disableLocalFilter?: boolean;
  /** When true, disabled items can be removed (chips, row, Clear all). Default keeps locked selections. */
  allowClearingDisabledSelections?: boolean;
  /** Merge the search field into the items list (shared border, no gap) for a compact look. */
  collapsed?: boolean;
  /**
   * Let the options list grow to fill its container's height instead of capping at a fixed
   * `max-h-70`. Only correct when the parent already resolves to a definite height (e.g. a
   * fullscreen mobile dialog's body) — otherwise the flex children collapse to content size,
   * which is harmless but pointless. Off by default so existing non-dialog embeds are untouched.
   */
  fillHeight?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  searchPlaceholder: undefined,
  disabled: false,
  useSecondaryColor: false,
  multiple: true,
  dotOnly: false,
  isFetchingNextPage: false,
  disableLocalFilter: false,
  allowClearingDisabledSelections: false,
  collapsed: false,
  fillHeight: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: (string | number)[]): void;
  (e: "fetch-next-page", isIntersecting: boolean): void;
  (e: "update:search", value: string): void;
}>();

const { t } = useI18n();

const searchQuery = ref("");
const selectedIds = ref<(string | number)[]>([...props.modelValue]);
const knownItems = ref(new Map<string | number, SearchableMultiselectItem>());

watch(
  () => props.modelValue,
  (value) => {
    selectedIds.value = [...value];
  },
  { immediate: true }
);

watch(
  () => props.items,
  (items) => {
    for (const item of items) knownItems.value.set(item.id, item);
  },
  { immediate: true }
);

watch(searchQuery, (value) => {
  if (props.disableLocalFilter) emit("update:search", value);
});

const emitUpdate = () => emit("update:modelValue", [...selectedIds.value]);

const filteredItems = computed<SearchableMultiselectItem[]>(() => {
  const list = props.items || [];
  const listIds = new Set(list.map((i) => i.id));

  const missingSelected = selectedIds.value
    .map((id) => knownItems.value.get(id))
    .filter((item): item is SearchableMultiselectItem => !!item && !listIds.has(item.id));

  const merged = [...missingSelected, ...list];

  if (props.disableLocalFilter) return merged;

  const query = (searchQuery.value || "").trim().toLowerCase();
  if (!query) return merged.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return merged
    .filter((item) => (item.name || "").toLowerCase().includes(query))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
});

const selectedItems = computed<SearchableMultiselectItem[]>(() => {
  return selectedIds.value
    .map((id) => knownItems.value.get(id))
    .filter((item): item is SearchableMultiselectItem => !!item)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
});

const toggleItem = (id: string | number) => {
  const item = knownItems.value.get(id);
  if (item?.disabled && !props.allowClearingDisabledSelections) return;
  if (item?.disabled && props.allowClearingDisabledSelections && !isSelected(id)) return;
  if (props.multiple) {
    const idx = selectedIds.value.indexOf(id);
    if (idx === -1) selectedIds.value.push(id);
    else selectedIds.value.splice(idx, 1);
  } else {
    selectedIds.value = isSelected(id) ? [] : [id];
  }
  emitUpdate();
};

const isSelected = (id: string | number) => selectedIds.value.includes(id);

const clearAll = () => {
  if (props.allowClearingDisabledSelections) {
    selectedIds.value = [];
  } else {
    selectedIds.value = selectedIds.value.filter((id) => knownItems.value.get(id)?.disabled);
  }
  emitUpdate();
};

const onScroll = (event: Event) => {
  if (props.isFetchingNextPage) return;
  const el = event.target as HTMLElement;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
    emit("fetch-next-page", true);
  }
};

const itemColor = (item: SearchableMultiselectItem) => item.color || DEFAULT_COLOR;

const selectedRowBackground = (item: SearchableMultiselectItem) => {
  if (props.useSecondaryColor) return "rgba(var(--v-theme-secondary), 0.1)";
  if (props.dotOnly) return undefined;
  const hex = itemColor(item);
  return `#${hex}1a`;
};

const showDot = (item: SearchableMultiselectItem) =>
  props.dotOnly ? true : !props.useSecondaryColor && !!item.color;

/** When dotOnly, the dot carries item color — keep the checkbox on theme tokens. */
const checkboxColor = (item: SearchableMultiselectItem) =>
  props.useSecondaryColor || props.dotOnly ? "secondary" : `#${itemColor(item)}`;
</script>

<template>
  <div
    :class="[
      { 'tw:pointer-events-none tw:opacity-60': props.disabled },
      fillHeight ? 'tw:h-full tw:flex tw:flex-col tw:min-h-0' : '',
    ]"
  >
    <v-text-field
      v-model="searchQuery"
      :placeholder="searchPlaceholder ?? t('generic.fields.multiselect.searchPlaceholder')"
      variant="outlined"
      density="comfortable"
      bg-color="surface"
      clearable
      prepend-inner-icon="mdi-magnify"
      hide-details
      class="tw:w-full"
      :class="[collapsed ? 'search-collapsed' : 'tw:!mb-4', fillHeight ? 'tw:!flex-none' : '']"
    />

    <div
      v-if="multiple && !collapsed && selectedItems.length"
      class="tw:!mb-4 tw:!p-3 tw:bg-surface-muted tw:rounded-lg tw:border tw:border-on-surface/10"
      :class="fillHeight ? 'tw:shrink-0' : ''"
    >
      <div class="tw:flex tw:items-center tw:justify-between tw:!mb-2">
        <span
          class="tw:text-xs tw:font-semibold tw:text-on-surface/70 tw:uppercase tw:tracking-wide"
        >
          {{ t("generic.fields.multiselect.selectedCount", { count: selectedItems.length }) }}
        </span>
        <v-btn
          v-if="multiple"
          variant="text"
          size="x-small"
          class="tw:!text-xs tw:!text-on-surface/50"
          @click="clearAll"
        >
          {{ t("generic.fields.multiselect.clearAll") }}
        </v-btn>
      </div>
      <div class="tw:flex tw:flex-wrap tw:gap-1.5 tw:min-w-0">
        <v-chip
          v-for="item in selectedItems"
          :key="item.id"
          size="small"
          :closable="multiple && (allowClearingDisabledSelections || !item.disabled)"
          :variant="props.useSecondaryColor ? 'tonal' : 'outlined'"
          :color="props.useSecondaryColor ? 'secondary' : undefined"
          class="tw:!text-xs tw:!font-medium tw:!max-w-full tw:min-w-0"
          @click:close="toggleItem(item.id)"
        >
          <template
            v-if="showDot(item)"
            #prepend
          >
            <div
              class="tw:w-3 tw:h-3 tw:rounded-full tw:!mr-2 tw:flex-shrink-0"
              :style="{ backgroundColor: '#' + itemColor(item) }"
            />
          </template>
          <span class="tw:whitespace-normal tw:break-words tw:text-left">{{ item.name }}</span>
        </v-chip>
      </div>
    </div>

    <div
      class="tw:border tw:border-on-surface/10 tw:overflow-hidden"
      :class="[
        collapsed ? 'tw:rounded-b-lg tw:border-t-0 list-collapsed' : 'tw:rounded-lg',
        fillHeight ? 'tw:flex tw:flex-col tw:flex-1 tw:min-h-0' : '',
      ]"
    >
      <div
        class="tw:overflow-y-auto"
        :class="fillHeight ? 'tw:flex-1 tw:min-h-0' : 'tw:max-h-70'"
        @scroll="onScroll"
      >
        <template v-if="multiple">
          <div
            v-for="item in filteredItems"
            :key="item.id"
            class="tw:flex tw:items-center tw:gap-3 tw:!px-4 tw:!py-3 tw:border-b last:tw:border-b-0 tw:border-on-surface/10 tw:transition-all"
            :class="[
              item.disabled
                ? 'tw:opacity-50 tw:cursor-not-allowed'
                : 'tw:cursor-pointer hover:tw:bg-on-surface/5',
              isSelected(item.id) && !selectedRowBackground(item) ? 'tw:bg-on-surface/5' : '',
            ]"
            :style="
              isSelected(item.id) && selectedRowBackground(item)
                ? { backgroundColor: selectedRowBackground(item) }
                : undefined
            "
            @click="toggleItem(item.id)"
            data-testid="select-item"
          >
            <v-checkbox
              :model-value="isSelected(item.id)"
              hide-details
              density="compact"
              :color="checkboxColor(item)"
              base-color="on-surface"
              class="tw:!my-0 tw:pointer-events-none"
            />
            <div
              v-if="showDot(item)"
              class="tw:w-3 tw:h-3 tw:rounded-full tw:flex-shrink-0"
              :style="{ backgroundColor: '#' + itemColor(item) }"
            />
            <div class="tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0 tw:overflow-hidden">
              <span
                class="tw:block tw:text-sm tw:text-on-surface/90 tw:!font-medium tw:!tracking-normal tw:break-words tw:whitespace-normal tw:w-full tw:text-left"
              >
                {{ item.name }}
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="item in filteredItems"
            :key="item.id"
            class="tw:flex tw:items-center tw:gap-3 tw:!px-4 tw:!py-3 tw:border-b last:tw:border-b-0 tw:border-on-surface/10 tw:transition-all tw:min-h-[52px]"
            :class="
              item.disabled
                ? 'tw:opacity-50 tw:cursor-not-allowed'
                : !isSelected(item.id)
                  ? 'tw:cursor-pointer hover:tw:bg-on-surface/5'
                  : 'tw:cursor-pointer'
            "
            :style="
              isSelected(item.id) && selectedRowBackground(item)
                ? { backgroundColor: selectedRowBackground(item) }
                : isSelected(item.id)
                  ? { backgroundColor: 'rgb(var(--v-theme-surface-muted))' }
                  : undefined
            "
            @click="toggleItem(item.id)"
            data-testid="select-item"
          >
            <div
              class="tw:w-5 tw:h-5 tw:rounded-full tw:flex-shrink-0 tw:border-2 tw:transition-colors"
              :class="
                isSelected(item.id)
                  ? props.useSecondaryColor
                    ? 'tw:border-secondary tw:bg-secondary'
                    : ''
                  : 'tw:border-on-surface/25'
              "
              :style="
                isSelected(item.id) && !props.useSecondaryColor
                  ? { borderColor: '#' + itemColor(item), backgroundColor: '#' + itemColor(item) }
                  : undefined
              "
            />
            <div
              v-if="showDot(item)"
              class="tw:w-3 tw:h-3 tw:rounded-full tw:flex-shrink-0"
              :style="{ backgroundColor: '#' + itemColor(item) }"
            />
            <div class="tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0 tw:overflow-hidden">
              <span
                class="tw:block tw:text-sm tw:text-on-surface/90 tw:!font-medium tw:!tracking-normal tw:break-words tw:whitespace-normal tw:w-full tw:text-left"
              >
                {{ item.name }}
              </span>
            </div>
          </div>
        </template>
        <div
          v-if="!filteredItems.length"
          class="tw:!px-4 tw:!py-8 tw:text-center tw:text-sm tw:text-on-surface/50"
        >
          {{ t("generic.fields.multiselect.noResults") }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-chip) {
  min-width: 0;
  max-width: 100%;
  height: auto !important;
  padding: 6px 16px !important;

  .v-chip__content {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
  }
}

/* Collapsed mode: merge the search field into the items list below it. */
.search-collapsed {
  :deep(.v-field) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.list-collapsed {
  /* Pull up so the shared border reads as a single line between search and list. */
  margin-top: -1px;
}
</style>
